/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 * ============================================================================= */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  AppShellV2,
  Button,
  Skeleton,
  TopBar,
} from '@skyxops/ui';
import { ShieldCheck, X } from 'lucide-react';
import { Branding, getTiles, mfaStatus, setToken, TilesResponse } from '../api';
import { Tile } from '../components/Tile';
import { useTopBarSlots } from '../components/TopBarContents';

const MFA_NUDGE_KEY = 'skyxops-mfa-nudge-dismissed';

interface Props {
  branding: Branding | null;
  onLogout: () => void;
}

export function TileGridPage({ branding, onLogout }: Props) {
  const [data, setData] = useState<TilesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  // Optional MFA nudge: suggest (never force) enabling 2FA; dismissible.
  const [showMfaNudge, setShowMfaNudge] = useState(false);

  useEffect(() => {
    getTiles()
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)));
    if (localStorage.getItem(MFA_NUDGE_KEY) !== '1') {
      mfaStatus().then((s) => setShowMfaNudge(!s.enabled)).catch(() => { /* ignore */ });
    }
  }, []);

  function dismissMfaNudge() {
    localStorage.setItem(MFA_NUDGE_KEY, '1');
    setShowMfaNudge(false);
  }

  function logout() {
    setToken(null);
    onLogout();
  }

  const { left, right, banner, headerStyle } = useTopBarSlots({
    principal: data?.principal ?? null,
    branding,
    onLogout: logout,
  });

  return (
    <AppShellV2
      topBar={<TopBar left={left} right={right} banner={banner} style={headerStyle} />}
    >
      {showMfaNudge && (
        <div className="mb-6 flex items-center gap-3 rounded-xl border border-skyxops-beach/40 bg-skyxops-beach/10 px-4 py-3">
          <ShieldCheck className="h-5 w-5 flex-none text-skyxops-beach" />
          <div className="flex-1 text-sm">
            <span className="font-medium">Protect your account.</span>{' '}
            <span className="text-muted-foreground">Add two-factor authentication for stronger sign-in security.</span>
          </div>
          <Button size="sm" className="bg-skyxops-midnight text-white hover:bg-skyxops-midnight/90"
            onClick={() => navigate('/profile#security')}>
            Set up 2FA
          </Button>
          <button type="button" aria-label="Dismiss" onClick={dismissMfaNudge}
            className="rounded p-1 text-muted-foreground hover:bg-black/5 dark:hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <h2 className="mb-6 text-xl font-semibold">
        <FormattedMessage id="tiles.heading" />
      </h2>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>
            <FormattedMessage id="tiles.load_error_title" defaultMessage="Failed to load apps" />
          </AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : !data ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[88px] w-full rounded-xl" />
          ))}
        </div>
      ) : data.tiles.length === 0 ? (
        <Alert>
          <AlertTitle>
            <FormattedMessage id="tiles.empty" />
          </AlertTitle>
          <AlertDescription>
            <FormattedMessage
              id="tiles.empty_hint"
              defaultMessage="Contact your administrator to enable apps for this account."
            />
          </AlertDescription>
        </Alert>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {data.tiles.map((t) => (
            <Tile key={t.app_id} tile={t} />
          ))}
        </div>
      )}
    </AppShellV2>
  );
}
